import {CssVarsProvider as JoyCssVarsProvider, extendTheme, StyleOverrides, Theme} from "@mui/joy";
import {PropsWithChildren} from "react";
import {EntityCapsuleProps} from "./entity/capsule.tsx";
import {AgentCapsuleProps} from "./agent/capsule.tsx";
import {CompanyCapsuleProps} from "./company/capsule.tsx";
import {VenueCapsuleProps} from "./venue/capsule.tsx";
import {ShinwaJobCapsuleProps} from "./job/capsule.tsx";
import {TruckCapsuleProps} from "./truck/capsule.tsx";
import {TruckModelCapsuleProps} from "./truck-model/capsule.tsx";
import {WasteFlavorCapsuleProps} from "./wasteFlavor/capsule.tsx";
import {TruckMountButtonProps} from "./driver/TruckMountButton.tsx";
import {OverridableRecord} from "@mui/joy/styles/types/utils";
import {PaletteRange} from "@mui/joy/styles/types/colorSystem";
import {ActionButtonProps} from "./components/ActionButton.tsx";
import {VeiledProps} from "./components/Veil.tsx";
import {
    createTheme as createMaterialTheme,
    THEME_ID as MATERIAL_THEME_ID,
    ThemeProvider as MaterialThemeProvider
} from "@mui/material/styles";
import {BundleCapsuleProps} from "./bundle/capsule.tsx";

const theme = extendTheme({
    fontFamily: {
        display: "Shippori Antique",
        body: "Shippori Antique",
    },
    colorSchemes: {
        light: {
            palette: {
                primary: {
                    "50": "#e4f3fd",
                    "100": "#bce1fc",
                    "200": "#93cffb",
                    "300": "#68bcf9",
                    "400": "#45aef8",
                    "500": "#23a0f7",
                    "600": "#1c92e9",
                    "700": "#147fd7",
                    "800": "#0d6ec5",
                    "900": "#0050a6",
                },
                secondary: {
                    "50": "#EDE7F6",
                    "100": "#D1C4E9",
                    "200": "#B39DDB",
                    "300": "#9575CD",
                    "400": "#7E57C2",
                    "500": "#673AB7",
                    "600": "#5E35B1",
                    "700": "#512DA8",
                    "800": "#4527A0",
                    "900": "#311B92",
                    // Adjust the global variant tokens as you'd like.
                    // The tokens should be the same for all color schemes.
                    solidBg: "var(--joy-palette-secondary-400)",
                    solidActiveBg: "var(--joy-palette-secondary-500)",
                    outlinedBorder: "var(--joy-palette-secondary-500)",
                    outlinedColor: "var(--joy-palette-secondary-700)",
                    outlinedActiveBg: "var(--joy-palette-secondary-100)",
                    softColor: "var(--joy-palette-secondary-800)",
                    softBg: "var(--joy-palette-secondary-200)",
                    softActiveBg: "var(--joy-palette-secondary-300)",
                    plainColor: "var(--joy-palette-secondary-700)",
                    plainActiveBg: "var(--joy-palette-secondary-100)",
                },
                tertiary: {
                    "50": "#FBE9E7",
                    "100": "#FFCCBC",
                    "200": "#FFAB91",
                    "300": "#FF8A65",
                    "400": "#FF7043",
                    "500": "#FF5722",
                    "600": "#F4511E",
                    "700": "#E64A19",
                    "800": "#D84315",
                    "900": "#BF360C",
                    // Adjust the global variant tokens as you'd like.
                    // The tokens should be the same for all color schemes.
                    solidBg: "var(--joy-palette-tertiary-400)",
                    solidActiveBg: "var(--joy-palette-tertiary-500)",
                    outlinedBorder: "var(--joy-palette-tertiary-500)",
                    outlinedColor: "var(--joy-palette-tertiary-700)",
                    outlinedActiveBg: "var(--joy-palette-tertiary-100)",
                    softColor: "var(--joy-palette-tertiary-800)",
                    softBg: "var(--joy-palette-tertiary-200)",
                    softActiveBg: "var(--joy-palette-tertiary-300)",
                    plainColor: "var(--joy-palette-tertiary-700)",
                    plainActiveBg: "var(--joy-palette-tertiary-100)",

                }
            },
        }
    },
    variants: {
    }
});

const materialTheme = createMaterialTheme({
    cssVariables: true,
    colorSchemes: { light: true, dark: true },
});

declare module "@mui/joy/styles" {
    export interface PaletteSecondaryOverrides {
    }

    export type PaletteSecondary = OverridableRecord<PaletteRange, PaletteSecondaryOverrides, string>
     
    export interface PaletteTertiaryOverrides {
    }

    export type PaletteTertiary = OverridableRecord<PaletteRange, PaletteTertiaryOverrides, string>

    interface Palette {
        secondary: PaletteSecondary;
        tertiary: PaletteTertiary;
    }

    interface ColorPalettePropOverrides {
        secondary: true;
        tertiary: true;
    }

    interface Components {
        HCBox?: {
            defaultProps?: Partial<PropsWithChildren>;
            styleOverrides?: StyleOverrides<"root", PropsWithChildren, Theme>;
        },
        // Navistar?: {
        //     defaultProps?: Partial<NavistarProps>;
        //     styleOverrides?: StyleOverrides<"root", NavistarProps, Theme>;
        // },
        // FontAwesomeJoyIcon?: {
        //     defaultProps?: Partial<FontAwesomeJoyIconProps>;
        //     styleOverrides?: StyleOverrides<"root", FontAwesomeJoyIconProps, Theme>;
        // },
        ActionButton?: {
            defaultProps?: Partial<ActionButtonProps>;
            styleOverrides?: StyleOverrides<"root", ActionButtonProps, Theme>;
        },
        EntityCapsule?: {
            defaultProps?: Partial<EntityCapsuleProps>;
            styleOverrides?: StyleOverrides<"root", EntityCapsuleProps, Theme>;
        }
        AgentCapsule?: {
            defaultProps?: Partial<AgentCapsuleProps>;
            styleOverrides?: StyleOverrides<"root", AgentCapsuleProps, Theme>;
        },
        CompanyCapsule?: {
            defaultProps?: Partial<CompanyCapsuleProps>;
            styleOverrides?: StyleOverrides<"root", CompanyCapsuleProps, Theme>;
        },
        VenueCapsule?: {
            defaultProps?: Partial<VenueCapsuleProps>;
            styleOverrides?: StyleOverrides<"root", VenueCapsuleProps, Theme>;
        },
        ShinwaJobCapsule?: {
            defaultProps?: Partial<ShinwaJobCapsuleProps>;
            styleOverrides?: StyleOverrides<"root", ShinwaJobCapsuleProps, Theme>;
        },
        // ShinwaJobCard?: {
        //     defaultProps?: Partial<ShinwaJobCardProps>;
        //     styleOverrides?: StyleOverrides<"root", ShinwaJobCapsuleProps, Theme>;
        // }
        TruckCapsule?: {
            defaultProps?: Partial<TruckCapsuleProps>;
            styleOverrides?: StyleOverrides<"root", TruckCapsuleProps, Theme>;
        },
        TruckModelCapsule?: {
            defaultProps?: Partial<TruckModelCapsuleProps>;
            styleOverrides?: StyleOverrides<"root", TruckModelCapsuleProps, Theme>;
        },
        BundleCapsule?: {
            defaultProps?: Partial<BundleCapsuleProps>;
            styleOverrides?: StyleOverrides<"root", BundleCapsuleProps, Theme>;
        },
        WasteFlavorCapsule?: {
            defaultProps?: Partial<WasteFlavorCapsuleProps>;
            styleOverrides?: StyleOverrides<"root", WasteFlavorCapsuleProps, Theme>;
        },
        TruckMountButton?: {
            defaultProps?: Partial<TruckMountButtonProps>;
            styleOverrides?: StyleOverrides<"root", TruckMountButtonProps, Theme>;
        },
        Veiled?: {
            defaultProps?: Partial<VeiledProps>;
            styleOverrides?: StyleOverrides<"root", VeiledProps, Theme>;
        }
    }
}

export function AppThemeProvider({children}: PropsWithChildren) {
    return <MaterialThemeProvider theme={{[MATERIAL_THEME_ID]: materialTheme}}>
        <JoyCssVarsProvider theme={theme}>
            {children}
        </JoyCssVarsProvider>
    </MaterialThemeProvider>
}
